import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, parseApiError, type Me } from '../lib/api';
import { orgRoleDisplayName } from '../lib/roles';
import { normalizeOrgMembersResponse } from '../lib/orgMembersPaging';

import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { AdminTiersPanel } from './AdminTiers';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';
import {
  Crown,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  FlaskConical,
  Coins,
  ArrowDown,
  Gift,
  Settings,
  Search,
  Activity,
  Beaker,
  Trash2,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// FounderAdmin — the platform-wide "god tools" page.
//
// Split out of AdminPanel.tsx (2026-07-17, Lisa's call) so the day-to-day ops
// page (disputes/flags/matches/stage/competitions) isn't sharing a screen
// with real-spend + debug tooling. Founder-only — the page-level gate below
// is defense-in-depth; every endpoint these sections hit is independently
// founder-locked server-side.
// ─────────────────────────────────────────────────────────────────────────────

// Never show a raw discordId as a person's name — same shortId idiom as
// OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/AdminTiers/AdminPanel.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

// ── Expandable Org Row (used in god view) ──────────────────────────────────

function OrgRow({ org, queryClient }: { org: { orgId: string; orgName: string; balance: number; totalPurchased: number; totalUsed: number }; queryClient: ReturnType<typeof useQueryClient> }) {
  const [expanded, setExpanded] = useState(false);
  const [grantAmt, setGrantAmt] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Page one only (god-view expand row, no pagination UI here) — both-shapes
  // tolerant via normalizeOrgMembersResponse (see lib/orgMembersPaging.ts).
  const { data: pageOneRaw, isLoading: membersLoading } = useQuery({
    queryKey: ['org-members', org.orgId],
    queryFn: () => api.orgMembers(org.orgId),
    enabled: expanded,
  });
  const members = normalizeOrgMembersResponse(pageOneRaw).members;

  const grantMut = useMutation({
    mutationFn: (amount: number) => api.admin.grantCredits(org.orgId, amount, 'Founder grant'),
    onSuccess: (data) => {
      // endpoint returns balanceAfter; newBalance kept as fallback for any
      // older response shape — this line used to print 'undefined'
      setSuccess(`Granted — new balance: ${(data as { balanceAfter?: number; newBalance?: number }).balanceAfter ?? data.newBalance}`);
      setGrantAmt('');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-org-credits'] });
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setSuccess(''); },
  });

  const isLow = org.balance <= 10;
  const isEmpty = org.balance === 0;

  return (
    <div>
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center justify-between px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-ink-meta shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-ink-meta shrink-0" />}
          <span className="text-sm text-white truncate">{org.orgName}</span>
          {isEmpty && <span className="rounded-full bg-red-500/20 px-1.5 py-0.5 text-[10px] font-bold text-red-400">EMPTY</span>}
          {isLow && !isEmpty && <span className="rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">LOW</span>}
        </div>
        <div className="flex items-center gap-4 text-xs shrink-0">
          <span className="text-ink-meta">P: {org.totalPurchased}</span>
          <span className="text-ink-meta">U: {org.totalUsed}</span>
          <span className={`font-bold min-w-[2rem] text-right ${org.balance < 0 ? 'text-red-400' : org.balance === 0 ? 'text-ink-meta' : 'text-green-400'}`}>
            {org.balance}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="bg-white/[0.02] px-4 py-3 space-y-3">
          {/* Inline grant */}
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-wider text-ink-meta mb-1">Grant credits to {org.orgName}</label>
              <div className="flex flex-wrap gap-2">
                <input
                  type="number"
                  min="1"
                  value={grantAmt}
                  onChange={e => { setGrantAmt(e.target.value); setError(''); setSuccess(''); }}
                  placeholder="Amount"
                  className="input w-20 text-sm py-1"
                />
                <button
                  onClick={() => { const n = parseInt(grantAmt); if (n > 0) grantMut.mutate(n); }}
                  disabled={grantMut.isPending || !grantAmt}
                  className="rounded-md bg-accent-500 px-3 py-1 text-xs font-bold text-[#0a0a0c] hover:bg-accent-400 disabled:opacity-40"
                >
                  {grantMut.isPending ? '...' : 'Grant'}
                </button>
                {[50, 100, 500].map(q => (
                  <button
                    key={q}
                    onClick={() => grantMut.mutate(q)}
                    disabled={grantMut.isPending}
                    className="rounded-md bg-white/[0.05] px-2 py-1 text-xs text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary disabled:opacity-40"
                  >
                    +{q}
                  </button>
                ))}
              </div>
            </div>
            <Link
              to={`/orgs/${encodeURIComponent(org.orgId)}/credits`}
              className="rounded-md bg-white/[0.05] px-2.5 py-1 text-xs text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary"
            >
              Full ledger
            </Link>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          {success && <p className="flex items-center gap-1 text-xs text-green-400"><CheckCircle2 className="h-3 w-3" /> {success}</p>}

          {/* Members */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-ink-meta mb-1.5">Members</p>
            {membersLoading ? (
              <p className="text-xs text-ink-meta">Loading...</p>
            ) : !members || members.length === 0 ? (
              <p className="text-xs text-ink-meta">No members in this org.</p>
            ) : (
              <div className="space-y-1">
                {members.map(m => (
                  <div key={m.discordId} className="flex items-center gap-3 rounded-md bg-white/[0.04] px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {(m.displayName ?? m.gamertag) ? (
                          <span className="text-sm font-medium text-white truncate">{m.displayName ?? m.gamertag}</span>
                        ) : (
                          <span className="text-sm font-mono text-ink-secondary truncate">{shortId(m.discordId)}</span>
                        )}
                        {m.clanName && (
                          <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-ink-meta truncate">{m.clanName}</span>
                        )}
                      </div>
                      {(m.displayName ?? m.gamertag) && (
                        <p className="font-mono text-[10px] text-ink-meta truncate">{shortId(m.discordId)}</p>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold uppercase shrink-0 ${
                      m.role === 'ORG_OWNER' ? 'text-yellow-400' :
                      m.role === 'ORG_ADMIN' ? 'text-accent-500' :
                      'text-ink-meta'
                    }`}>
                      {orgRoleDisplayName(m.role)}
                    </span>
                    {m.joinedAt && (
                      <span className="text-[10px] text-ink-meta shrink-0">{new Date(m.joinedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TokenGrantSection — founder-only.
//
// Two side-by-side cards:
//   1. Quick self-grant — prefilled with the founder's own discordId, just
//      enter amount + click. Single-click flow for "I need to top up to test".
//   2. Generic grant — pick scope (user/clan/org/platform), enter targetId
//      + tokens + reason. Audit trail row written on submit, balance shown.
//
// Hits POST /admin/credits/grant (the founder-only endpoint). 100k tokens
// per-call cap enforced server-side; client surfaces the validation error.
// ─────────────────────────────────────────────────────────────────────────────

function TokenGrantSection({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [selfAmount, setSelfAmount] = useState('');
  const [selfReason, setSelfReason] = useState('Self top-up');
  const [selfMsg, setSelfMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [genScope, setGenScope] = useState<'user' | 'clan' | 'org' | 'platform'>('user');
  const [genTargetId, setGenTargetId] = useState('');
  const [genAmount, setGenAmount] = useState('');
  const [genReason, setGenReason] = useState('');
  const [genMsg, setGenMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const selfMut = useMutation({
    mutationFn: () => api.admin.grantTokens({
      scope: 'user',
      targetId: me.discordId,
      tokens: Number(selfAmount),
      reason: selfReason.trim() || 'Self top-up',
    }),
    onSuccess: (res) => {
      setSelfMsg({ kind: 'ok', text: `+${res.granted} → balance ${res.balanceAfter}` });
      setSelfAmount('');
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => setSelfMsg({ kind: 'err', text: parseApiError(e).message }),
  });

  const genMut = useMutation({
    mutationFn: () => api.admin.grantTokens({
      scope: genScope,
      targetId: genScope === 'platform' ? undefined : genTargetId.trim(),
      tokens: Number(genAmount),
      reason: genReason.trim(),
    }),
    onSuccess: (res) => {
      setGenMsg({ kind: 'ok', text: `+${res.granted} to ${res.scope}${res.targetId ? `:${res.targetId}` : ''} → balance ${res.balanceAfter}` });
      setGenAmount('');
      setGenTargetId('');
      setGenReason('');
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'platform-credits'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-org-credits'] });
    },
    onError: (e: Error) => setGenMsg({ kind: 'err', text: parseApiError(e).message }),
  });

  if (me.role !== 'founder') return null;

  const selfDisabled = selfMut.isPending || !Number(selfAmount) || Number(selfAmount) <= 0;
  const genDisabled = genMut.isPending
    || !Number(genAmount) || Number(genAmount) <= 0
    || (genScope !== 'platform' && !genTargetId.trim())
    || !genReason.trim();

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Self-grant card — single-click founder top-up */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Gift className="h-5 w-5 text-emerald-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Grant myself</h2>
        </div>
        <p className="text-xs text-ink-meta mb-3">
          Add tokens to your own user balance. Audit row created with grantedBy = your Discord ID.
        </p>
        <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-center">
          <input
            type="number"
            min="1"
            value={selfAmount}
            onChange={e => setSelfAmount(e.target.value)}
            placeholder="Tokens"
            className="input w-full sm:w-32"
          />
          <input
            type="text"
            value={selfReason}
            onChange={e => setSelfReason(e.target.value)}
            placeholder="Reason"
            className="input w-full sm:flex-1"
          />
          <button
            disabled={selfDisabled}
            onClick={() => { setSelfMsg(null); selfMut.mutate(); }}
            className="w-full sm:w-auto rounded-lg bg-emerald-500/15 px-4 py-2 text-sm font-bold uppercase tracking-wider text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40"
          >
            {selfMut.isPending ? '…' : 'Grant'}
          </button>
        </div>
        <p className="text-[10px] text-ink-meta">Granting to USER#{me.discordId.slice(0, 12)}…</p>
        {selfMsg && (
          <p className={`mt-2 text-xs ${selfMsg.kind === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
            {selfMsg.text}
          </p>
        )}
      </Card>

      {/* Generic grant card — any scope */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Coins className="h-5 w-5 text-accent-500" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Grant to user / clan / org</h2>
        </div>
        <p className="text-xs text-ink-meta mb-3">
          Founder-only. 100,000 tokens max per grant. Every grant is logged immutably.
        </p>

        <div className="grid grid-cols-1 gap-2 mb-2 sm:grid-cols-2">
          <select
            value={genScope}
            onChange={e => setGenScope(e.target.value as 'user' | 'clan' | 'org' | 'platform')}
            className="input"
          >
            <option value="user">User</option>
            <option value="clan">Clan</option>
            <option value="org">Org</option>
            <option value="platform">Platform pool</option>
          </select>
          <input
            type="text"
            value={genTargetId}
            onChange={e => setGenTargetId(e.target.value)}
            placeholder={
              genScope === 'platform' ? '— (n/a for platform)'
                : genScope === 'user' ? 'Discord ID'
                : genScope === 'clan' ? 'Clan name (e.g. KARE — no CLAN# prefix needed)'
                : 'Org id (e.g. gg-dev — no ORG# prefix needed)'
            }
            disabled={genScope === 'platform'}
            className="input disabled:opacity-40"
          />
        </div>

        <div className="grid grid-cols-1 gap-2 mb-2 sm:grid-cols-2">
          <input
            type="number"
            min="1"
            value={genAmount}
            onChange={e => setGenAmount(e.target.value)}
            placeholder="Tokens"
            className="input"
          />
          <input
            type="text"
            value={genReason}
            onChange={e => setGenReason(e.target.value)}
            placeholder="Reason (required)"
            className="input"
          />
        </div>

        <button
          disabled={genDisabled}
          onClick={() => { setGenMsg(null); genMut.mutate(); }}
          className="btn-primary w-full uppercase tracking-wider"
        >
          {genMut.isPending ? 'Granting…' : 'Grant tokens'}
        </button>

        {genMsg && (
          <p className={`mt-2 text-xs ${genMsg.kind === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
            {genMsg.text}
          </p>
        )}
      </Card>
    </div>
  );
}

// ── Platform Credits Section (Founder-only god view) ────────────────────────

function PlatformCreditsSection({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [grantAmount, setGrantAmount] = useState('');
  const [capAmount, setCapAmount] = useState('');
  const [error, setError] = useState('');

  const { data: pc, isLoading: pcLoading } = useQuery({
    queryKey: ['admin', 'platform-credits'],
    queryFn: api.admin.platformCredits,
  });

  const { data: txs } = useQuery({
    queryKey: ['admin', 'platform-credits-tx'],
    queryFn: api.admin.platformCreditTransactions,
  });

  const { data: allOrgs } = useQuery({
    queryKey: ['admin', 'all-org-credits'],
    queryFn: api.admin.allOrgCredits,
  });

  const grantMut = useMutation({
    mutationFn: (amount: number) => api.admin.grantPlatformCredits(amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'platform-credits'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'platform-credits-tx'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      setGrantAmount('');
      setError('');
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const capMut = useMutation({
    mutationFn: (cap: number) => api.admin.updatePlatformSpendCap(cap),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'platform-credits'] });
      queryClient.invalidateQueries({ queryKey: ['me'] });
      setCapAmount('');
      setError('');
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (me.role !== 'founder') return null;

  const monthSpend = pc?.currentMonthSpend ?? 0;
  const cap = pc?.monthlySpendCap ?? 10000;
  const capPct = cap > 0 ? Math.min((monthSpend / cap) * 100, 100) : 0;

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Coins className="h-5 w-5 text-accent-500" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Platform Credits</h2>
      </div>

      {pcLoading ? <PageSpinner /> : (
        <div className="space-y-5">
          {/* Headline = monthly spend vs cap (the operationally meaningful number).
              Raw balance is unsettled until the nightly reconciliation job squares
              estimated spend against AWS Bedrock actuals — kept visible but small
              so it doesn't read as "the system is broken" pre-midnight. */}
          <div className="space-y-3 max-w-xl">
            <div>
              <div className="flex items-baseline gap-3 text-xs text-ink-meta mb-1">
                <span>Monthly spend</span>
                <span className="font-mono text-ink-body">{monthSpend} / {cap}</span>
              </div>
              <div className="h-3 rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${capPct > 80 ? 'bg-red-500' : capPct > 50 ? 'bg-amber-500' : 'bg-accent-500'}`}
                  style={{ width: `${capPct}%` }}
                />
              </div>
              <div className="flex gap-4 text-[10px] text-ink-meta mt-1">
                <span>Granted: {pc?.totalGranted ?? 0}</span>
                <span>Total used: {pc?.totalUsed ?? 0}</span>
              </div>
            </div>

            <div className="inline-flex items-center gap-4 rounded-md bg-white/[0.04] px-3 py-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-ink-meta">Unsettled balance</p>
                <p className={`text-lg font-semibold ${(pc?.balance ?? 0) < 0 ? 'text-red-300/80' : 'text-ink-body'}`}>
                  {pc?.balance ?? 0}
                </p>
              </div>
              <p className="text-[10px] text-ink-meta max-w-[18rem]">
                Pre-reconciliation. Nightly job at 00:00 squares estimated OCR spend against AWS Bedrock actuals.
              </p>
            </div>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          {/* Controls row */}
          <div className="flex flex-wrap gap-x-10 gap-y-3">
            <div>
              <label className="block text-xs text-ink-meta mb-1">Grant credits</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  value={grantAmount}
                  onChange={e => setGrantAmount(e.target.value)}
                  placeholder="Amount"
                  className="input w-24 text-sm py-1.5"
                />
                <button
                  onClick={() => { const n = parseInt(grantAmount); if (n > 0) grantMut.mutate(n); }}
                  disabled={grantMut.isPending}
                  className="rounded-md bg-accent-500 px-3 py-1.5 text-xs font-bold text-[#0a0a0c] hover:bg-accent-400 disabled:opacity-40"
                >
                  {grantMut.isPending ? 'Granting...' : 'Grant'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-ink-meta mb-1">Monthly spend cap</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  value={capAmount}
                  onChange={e => setCapAmount(e.target.value)}
                  placeholder={String(cap)}
                  className="input w-24 text-sm py-1.5"
                />
                <button
                  onClick={() => { const n = parseInt(capAmount); if (n > 0) capMut.mutate(n); }}
                  disabled={capMut.isPending}
                  className="rounded-md bg-white/[0.06] px-3 py-1.5 text-xs font-bold text-ink-body hover:bg-white/[0.1] disabled:opacity-40"
                >
                  <Settings className="h-3 w-3 inline mr-1" />
                  Set
                </button>
              </div>
            </div>
          </div>

          {/* All orgs — expandable with members + inline grant */}
          {allOrgs && allOrgs.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-meta mb-2">Org Management</p>
              <div className="overflow-hidden rounded-lg bg-white/[0.03] divide-y divide-white/[0.04]">
                {allOrgs.map(org => (
                  <OrgRow key={org.orgId} org={org} queryClient={queryClient} />
                ))}
              </div>
            </div>
          )}

          {/* Platform transactions */}
          {txs && txs.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-meta mb-2">Platform Transactions</p>
              <div className="max-h-48 overflow-y-auto rounded-lg bg-white/[0.03] divide-y divide-white/[0.04]">
                {txs.map(tx => (
                  <div key={tx.sk} className="flex items-center gap-2 px-3 py-2">
                    {tx.type === 'GRANT' ? <Gift className="h-3 w-3 text-accent-500 shrink-0" /> : <ArrowDown className="h-3 w-3 text-red-400 shrink-0" />}
                    <span className="text-xs text-ink-body truncate flex-1">{tx.reason}</span>
                    <span className={`text-xs font-bold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </span>
                    <span className="text-[10px] text-ink-meta">{tx.balanceAfter} bal</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Simulation Section (Founder-only) ──────────────────────────────────────

function SimulationSection() {
  const qc = useQueryClient();
  const [simName, setSimName] = useState('');
  const [clanCount, setClanCount] = useState('4');
  const [playersPerClan, setPlayersPerClan] = useState('5');
  const [result, setResult] = useState<{ compId: string; clans: string[]; playerCount: number } | null>(null);
  const [error, setError] = useState('');

  const createMut = useMutation({
    mutationFn: () => api.admin.simulateTournament(simName, parseInt(clanCount) || 4, parseInt(playersPerClan) || 5),
    onSuccess: (data) => {
      setResult(data);
      setError('');
      setSimName('');
      qc.invalidateQueries({ queryKey: ['competitions'] });
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setResult(null); },
  });

  const deleteMut = useMutation({
    mutationFn: (compId: string) => api.admin.deleteSimulation(compId),
    onSuccess: () => {
      setResult(null);
      setError('');
      qc.invalidateQueries({ queryKey: ['competitions'] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Beaker className="h-4 w-4 text-ink-secondary" /> Simulate Tournament
      </h2>
      <div className="space-y-3">
        <input
          type="text"
          placeholder="Tournament name (e.g. Test Series 1)"
          value={simName}
          onChange={e => { setSimName(e.target.value); setError(''); }}
          className="input w-full"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-ink-meta mb-1">Clans</label>
            <select value={clanCount} onChange={e => setClanCount(e.target.value)} className="input w-full">
              {[2, 4, 6, 8, 12, 16].map(n => <option key={n} value={n}>{n} clans</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-ink-meta mb-1">Players per clan</label>
            <select value={playersPerClan} onChange={e => setPlayersPerClan(e.target.value)} className="input w-full">
              {[3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n}v{n}</option>)}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <button
          onClick={() => createMut.mutate()}
          disabled={!simName.trim() || createMut.isPending}
          className="btn-primary w-full"
        >
          {createMut.isPending ? 'Creating...' : 'Create Simulated Tournament'}
        </button>
        {result && (
          <div className="rounded-lg bg-white/[0.04] p-3 space-y-2">
            <p className="text-xs text-ink-primary font-bold">Tournament created</p>
            <p className="text-xs text-ink-secondary font-mono">{result.compId}</p>
            <p className="text-xs text-ink-meta">{result.clans.length} clans, {result.playerCount} ghost seats</p>
            <button
              onClick={() => deleteMut.mutate(result.compId)}
              disabled={deleteMut.isPending}
              className="flex items-center gap-1 rounded-md bg-red-500/10 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/15 disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" /> {deleteMut.isPending ? 'Deleting...' : 'Teardown'}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Admin Tiers dropdown (Lisa, 2026-07-17) ────────────────────────────────
// Admin Tiers used to be its own sidebar entry / a jump-out link; now it lives
// here as a collapsible section so the whole platform-role surface sits on the
// Founder Admin page. Reuses AdminTiersPanel — the exact Admin-Tier-Rows +
// Legacy-Helper-Rows controls the standalone /admin/tiers page still renders
// (both HELPER and platform ADMIN are retired now — see AdminTiersPanel's
// own header comment, role-spine follow-up 2026-07-24 Directive 1).
function AdminTiersDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between text-left group"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <Crown className="h-5 w-5 text-yellow-400" />
          <div>
            <p className="text-sm font-semibold text-white group-hover:text-accent-500 transition-colors">Admin Tiers</p>
            <p className="text-xs text-ink-meta">HELPER and platform ADMIN are retired — read-only audit + legacy-row cleanup only.</p>
          </div>
        </div>
        {open
          ? <ChevronDown className="h-4 w-4 text-ink-meta" />
          : <ChevronRight className="h-4 w-4 text-ink-meta group-hover:text-accent-500 transition-colors" />}
      </button>
      {open && (
        <div className="mt-4 border-t border-white/[0.06] pt-4">
          <AdminTiersPanel />
        </div>
      )}
    </Card>
  );
}

// ── Founder Admin root ────────────────────────────────────────────────────────

export function FounderAdmin() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });

  if (!me || me.role !== 'founder') {
    return (
      <Card className="text-center">
        <p className="text-gray-400">Founder access required.</p>
      </Card>
    );
  }

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Crown className="h-7 w-7 text-yellow-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Founder Admin</h1>
          <p className="text-xs text-ink-meta">Platform-wide god tools — real spend, debug, simulations, role grants.</p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          id="founder-admin-intro"
          title="What lives here"
          intro="Platform-wide tools, founder-only. Everything here either moves real spend or is a debug/support surface — none of it is day-to-day competition ops (that's still the Admin Panel)."
          points={[
            { text: 'Token Grant and Platform Credits move real spend — grant credits, set the monthly cap, audit every transaction, and manage org balances.' },
            { text: 'User Lookup, Activity Log, OCR Screenshot Test, and Simulate Tournament are debug/support tools.' },
            { text: 'Admin Tiers is now read-only audit + legacy-helper-row cleanup — HELPER and platform ADMIN are both retired, nothing is appointable here any more.' },
          ]}
          className="mb-6"
        />
      </div>

      <div className={cn('space-y-6', SETTLE)} style={settleDelay(2)}>
        <TokenGrantSection me={me} />

        <PlatformCreditsSection me={me} />

        <Card>
          <Link to="/admin/user-lookup" className="flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <Search className="h-5 w-5 text-ink-secondary" />
              <div>
                <p className="text-sm font-semibold text-white group-hover:text-accent-500 transition-colors">User Lookup</p>
                <p className="text-xs text-ink-meta">Search any Discord user — view memberships, profile, gamertag, activity history</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-ink-meta group-hover:text-accent-500 transition-colors" />
          </Link>
        </Card>

        <Card>
          <Link to="/admin/activity" className="flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-ink-secondary" />
              <div>
                <p className="text-sm font-semibold text-white group-hover:text-accent-500 transition-colors">Activity Log</p>
                <p className="text-xs text-ink-meta">Global timeline of every tracked action — submissions, credit grants, member moves, stage advances</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-ink-meta group-hover:text-accent-500 transition-colors" />
          </Link>
        </Card>

        <Card>
          <Link
            to="/admin/ocr-test"
            className="flex items-center justify-between group"
          >
            <div className="flex items-center gap-3">
              <FlaskConical className="h-5 w-5 text-ink-secondary" />
              <div>
                <p className="text-sm font-semibold text-white group-hover:text-accent-500 transition-colors">OCR Screenshot Test</p>
                <p className="text-xs text-ink-meta">Upload any scoreboard screenshot to preview the exact image, parsed result, and raw OCR output</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-ink-meta group-hover:text-accent-500 transition-colors" />
          </Link>
        </Card>

        <AdminTiersDropdown />

        <SimulationSection />
      </div>
    </div>
  );
}
